import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

// Draws a venue layout (see the API venue model) and lets the buyer pick a seat.
// Everything is in venue canvas units. At 100% the whole venue fits the map area
// (give the component a definite height for that); zooming scrolls inside it.
// On narrow screens a tap first zooms in, so seats are big enough to hit.

const ZOOM_LEVELS = [1, 1.75, 2.5, 3.5, 5];
const TAP_ZOOM_INDEX = 2;
const COMPACT_WIDTH = 480;

export const seatKey = (zone, number) => `${zone}-${number}`;

export const padNumber = (number) => String(number).padStart(2, "0");

// "Yer 7" for seats, "Masa 7" for tables
export const unitLabel = (zone, number) => `${zone?.kind === "tables" ? "Masa" : "Yer"} ${number}`;

// Chairs drawn around a table as grey dots: spread over the four sides, top first
export const chairPositions = (capacity, size) => {
  const perSide = [0, 0, 0, 0]; // top, bottom, left, right
  for (let i = 0; i < capacity; i++) perSide[i % 4] += 1;
  const gap = size * 0.22;
  const along = (count, i) => ((i + 1) * size) / (count + 1);
  const points = [];
  for (let i = 0; i < perSide[0]; i++) points.push({ cx: along(perSide[0], i), cy: -gap });
  for (let i = 0; i < perSide[1]; i++) points.push({ cx: along(perSide[1], i), cy: size + gap });
  for (let i = 0; i < perSide[2]; i++) points.push({ cx: -gap, cy: along(perSide[2], i) });
  for (let i = 0; i < perSide[3]; i++) points.push({ cx: size + gap, cy: along(perSide[3], i) });
  return points;
};

const shapeCenter = (shape) =>
  shape.type === "rect"
    ? { cx: shape.x + (shape.width || 0) / 2, cy: shape.y + (shape.height || 0) / 2 }
    : { cx: shape.x, cy: shape.y };

// Stage, podium and labels of a venue; also used by the admin seat plan editor
export const Shape = ({ shape }) => {
  const { cx, cy } = shapeCenter(shape);
  const transform = shape.rotation ? `rotate(${shape.rotation} ${cx} ${cy})` : undefined;

  if (shape.type === "rect") {
    return (
      <g transform={transform}>
        <rect x={shape.x} y={shape.y} width={shape.width} height={shape.height} rx={8} className="fill-surface-800" />
        {shape.label && (
          <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fontSize={shape.fontSize || 28} className="fill-white font-semibold">
            {shape.label}
          </text>
        )}
      </g>
    );
  }

  return (
    <text x={shape.x} y={shape.y} transform={transform} textAnchor="middle" dominantBaseline="central" fontSize={shape.fontSize || 28} className="fill-surface-400 font-semibold">
      {shape.label}
    </text>
  );
};

// priceStyle: the seat's price color (see pricing.js)
const seatClasses = ({ isSelected, isTaken, isOnSale, priceStyle }) => {
  if (isSelected) return { rect: "fill-pink-600 stroke-pink-600", text: "fill-white" };
  if (isTaken) return { rect: "fill-surface-300 stroke-surface-300", text: "fill-surface-500" };
  if (!isOnSale || !priceStyle) return { rect: "fill-surface-100 stroke-surface-200", text: "fill-surface-300" };
  return { rect: `fill-white ${priceStyle.seat}`, text: priceStyle.text };
};

const Seat = ({ zone, seat, size, isSelected, isTaken, isOnSale, disabled, priceLabel, priceStyle, capacity, onSelect }) => {
  const isClickable = isOnSale && !isTaken && !disabled;
  const classes = seatClasses({ isSelected, isTaken, isOnSale, priceStyle });
  const status = isSelected ? "seçilib" : isTaken ? "satılıb" : !isOnSale ? "satışda deyil" : priceLabel;
  const isTable = zone.kind === "tables";
  const label = `${zone.name || zone.code}, ${unitLabel(zone, seat.number).toLowerCase()}${
    isTable && capacity ? `, ${capacity} nəfərlik` : ""
  }${status ? `, ${status}` : ""}`;

  const select = () => isClickable && onSelect(zone.code, seat);

  return (
    <g
      transform={`translate(${seat.x - size / 2} ${seat.y - size / 2})`}
      role="button"
      tabIndex={isClickable ? 0 : -1}
      aria-label={label}
      aria-pressed={isSelected}
      aria-disabled={!isClickable}
      onClick={select}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          select();
        }
      }}
      className={`group outline-none ${isClickable ? "cursor-pointer" : "cursor-not-allowed"}`}
    >
      <title>{label}</title>
      {isTable &&
        chairPositions(capacity || 0, size).map((chair, i) => (
          <circle key={i} cx={chair.cx} cy={chair.cy} r={size * 0.1} className="fill-surface-400" />
        ))}
      <rect width={size} height={size} rx={size * 0.2} strokeWidth={2} className={`transition-colors ${classes.rect}`} />
      <text
        x={size / 2}
        y={size / 2}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={size * 0.42}
        className={`font-semibold pointer-events-none select-none transition-colors ${classes.text}`}
      >
        {padNumber(seat.number)}
      </text>
    </g>
  );
};

const LegendItem = ({ className, label }) => (
  <span className="flex items-center gap-1.5">
    <span className={`w-3.5 h-3.5 rounded border-2 ${className}`} />
    {label}
  </span>
);

// Free seats are explained by the price list, whose colors match the seats
export const SeatLegend = ({ className = "" }) => (
  <div className={`flex flex-wrap items-center gap-3 text-xs text-surface-500 ${className}`}>
    <LegendItem className="bg-pink-600 border-pink-600" label="Seçilmiş" />
    <LegendItem className="bg-surface-300 border-surface-300" label="Satılıb" />
    <LegendItem className="bg-surface-100 border-surface-200" label="Satışda deyil" />
  </div>
);

// items: seatPricing.priceList (see pricing.js), one per ticket type on sale
export const PriceList = ({ items = [], className = "" }) => (
  <div className={`flex flex-wrap gap-2 ${className}`}>
    {items.map((item) => (
      <span key={item.key} className="flex items-center gap-1.5 text-xs font-medium bg-surface-100 text-surface-700 rounded-full px-3 py-1 whitespace-nowrap">
        <span className={`w-3 h-3 rounded border-2 bg-white ${item.style ? item.style.swatch : "border-surface-200"}`} />
        {item.label}
        {" — "}
        {item.price}
      </span>
    ))}
  </div>
);


// The map area: zoom buttons, and a scroll box the venue is drawn into. At 100%
// the whole venue fits; above that it scrolls. Children are given the zoom state
// so they can decide what a tap means — on a phone the first tap zooms in, since
// a seat drawn at 100% is far too small to hit.
export const MapViewport = ({ venue, className = "", hint, canvasStyle, children }) => {
  const [zoomIndex, setZoomIndex] = useState(0);
  const [isCompact, setIsCompact] = useState(false);
  const containerRef = useRef(null);
  const zoomCenterRef = useRef(null);
  const zoom = ZOOM_LEVELS[zoomIndex];

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    const update = () => setIsCompact(container.clientWidth < COMPACT_WIDTH);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [venue]);

  // Keep the requested venue point in the middle of the view after zooming
  useLayoutEffect(() => {
    const container = containerRef.current;
    const center = zoomCenterRef.current;
    if (!container || !center || !venue) return;
    zoomCenterRef.current = null;
    const scale = container.scrollWidth / venue.width;
    container.scrollLeft = center.x * scale - container.clientWidth / 2;
    container.scrollTop = center.y * scale - container.clientHeight / 2;
  }, [zoomIndex, venue]);

  const zoomTo = (index, center) => {
    const container = containerRef.current;
    if (!center) {
      // At 100% the venue is centered in the map area
      center = { x: venue.width / 2, y: venue.height / 2 };
      if (zoomIndex > 0 && container) {
        const scale = container.scrollWidth / venue.width;
        center = {
          x: (container.scrollLeft + container.clientWidth / 2) / scale,
          y: (container.scrollTop + container.clientHeight / 2) / scale,
        };
      }
    }
    zoomCenterRef.current = center;
    setZoomIndex(index);
  };

  const state = { zoom, zoomIndex, isCompact, zoomTo, tapZoomIndex: TAP_ZOOM_INDEX };

  if (!venue) return null;

  return (
    <div className={`flex flex-col min-h-0 ${className}`}>
      <div className="flex items-center justify-between gap-3 mb-2 text-xs text-surface-500">
        <p>{typeof hint === "function" ? hint(state) : hint}</p>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={() => zoomTo(Math.max(0, zoomIndex - 1))}
            disabled={zoomIndex === 0}
            aria-label="Kiçilt"
            className="w-8 h-8 rounded-lg bg-surface-100 hover:bg-surface-200 text-surface-700 font-bold disabled:opacity-40 disabled:cursor-not-allowed"
          >
            −
          </button>
          <span className="w-12 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
          <button
            type="button"
            onClick={() => zoomTo(Math.min(ZOOM_LEVELS.length - 1, zoomIndex + 1))}
            disabled={zoomIndex === ZOOM_LEVELS.length - 1}
            aria-label="Böyüt"
            className="w-8 h-8 rounded-lg bg-surface-100 hover:bg-surface-200 text-surface-700 font-bold disabled:opacity-40 disabled:cursor-not-allowed"
          >
            +
          </button>
        </div>
      </div>

      <div ref={containerRef} data-seat-map-scroll className="flex-1 min-h-0 overflow-auto rounded-2xl bg-surface-50 border border-surface-100">
        <svg
          viewBox={`0 0 ${venue.width} ${venue.height}`}
          style={{ ...(zoomIndex === 0 ? { width: "100%", height: "100%" } : { width: `${zoom * 100}%` }), ...canvasStyle }}
          className="block"
          role="group"
          aria-label={venue.name}
        >
          {typeof children === "function" ? children(state) : children}
        </svg>
      </div>
    </div>
  );
};

// seatPricing: buildSeatPricing(venue, ticketTypes); seats without a price are not on sale
const SeatMap = ({ venue, takenSeats = [], selectedSeat, selectedSeats, onSelect, seatPricing, disabled = false, className = "" }) => {
  const seatSize = venue?.seatSize || 30;

  const taken = useMemo(
    () => new Set(takenSeats.map(({ zone, number }) => seatKey(zone, number))),
    [takenSeats]
  );

  if (!venue) return null;

  // One seat or several: an order may cover up to a handful of seats
  const chosen = selectedSeats || (selectedSeat ? [selectedSeat] : []);
  const selectedKeys = new Set(chosen.map(({ zone, number }) => seatKey(zone, number)));

  return (
    <MapViewport
      venue={venue}
      className={className}
      hint={({ isCompact, zoomIndex, tapZoomIndex }) =>
        (isCompact && zoomIndex < tapZoomIndex ? "Yaxınlaşdırmaq üçün sxemə toxunun" : "")}
    >
      {({ isCompact, zoomIndex, zoomTo, tapZoomIndex }) => {
        // On a phone the first tap zooms in on the seat rather than picking it
        const handleSeatSelect = (zoneCode, seat) => {
          if (isCompact && zoomIndex < tapZoomIndex) {
            zoomTo(tapZoomIndex, { x: seat.x, y: seat.y });
            return;
          }
          onSelect(zoneCode, seat.number, seat);
        };

        return (
          <>
            {(venue.shapes || []).map((shape, i) => (
              <Shape key={i} shape={shape} />
            ))}
            {(venue.zones || []).map((zone) =>
              (zone.seats || []).map((seat) => {
                const key = seatKey(zone.code, seat.number);
                const info = seatPricing?.seatInfo(zone.code, seat);
                const capacity = zone.kind === "tables" ? seatPricing?.typesForSeat(zone.code, seat)[0]?.capacity : undefined;
                return (
                  <Seat
                    key={key}
                    zone={zone}
                    seat={seat}
                    size={seat.size || seatSize}
                    capacity={capacity}
                    isSelected={selectedKeys.has(key)}
                    isTaken={taken.has(key)}
                    isOnSale={Boolean(info?.price)}
                    disabled={disabled}
                    priceLabel={info?.price}
                    priceStyle={info?.style}
                    onSelect={handleSeatSelect}
                  />
                );
              })
            )}
          </>
        );
      }}
    </MapViewport>
  );
};

export default SeatMap;
